import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages the add-to-cart notification modal.
 * Shows a notification when items are added to cart with product details.
 *
 * @typedef {object} CartItem
 * @property {string} image - The product image URL.
 * @property {string} product_title - The product title.
 * @property {number} price - The product price in cents.
 * @property {string} variant_title - The variant title.
 * @property {Record<string, string>} properties - The product properties.
 * @property {Array<{name: string, value: string}>} options_with_values - The variant options.
 *
 * @typedef {object} AddToCartNotificationRefs
 * @property {DialogComponent} notificationDialog - The notification dialog component.
 * @property {HTMLImageElement} [productImage] - The product image element.
 * @property {HTMLElement} [productName] - The product name element.
 * @property {HTMLElement} [productPrice] - The product price element.
 * @property {HTMLElement} [productSize] - The product size element.
 * @property {HTMLElement} [productColor] - The product color element.
 * @property {HTMLAnchorElement} [checkoutButton] - The checkout button.
 * @property {HTMLButtonElement} [viewCartButton] - The view cart button.
 * @extends Component<AddToCartNotificationRefs>
 */
export class AddToCartNotificationComponent extends Component {
  requiredRefs = [
    'notificationDialog'
  ];

  /** @type {number | undefined} */
  #autoCloseTimeout;

  /** @type {() => void} */
  #closeButtonHandler;

  connectedCallback() {
    super.connectedCallback();
    
    try {
      document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
      
      // Handle close button click - wait for refs to be available
      this.#closeButtonHandler = () => this.close();
      
      // Use setTimeout to ensure refs are available after DOM is rendered
      setTimeout(() => {
        const closeButton = this.querySelector('.add-to-cart-notification__close');
        if (closeButton) {
          closeButton.addEventListener('click', this.#closeButtonHandler);
        }
      }, 0);
    } catch (error) {
      console.error('Error in add-to-cart-notification connectedCallback:', error);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    try {
      document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
      
      // Remove close button event listener
      const closeButton = this.querySelector('.add-to-cart-notification__close');
      if (closeButton && typeof this.#closeButtonHandler === 'function') {
        closeButton.removeEventListener('click', this.#closeButtonHandler);
      }
      
      if (this.#autoCloseTimeout) {
        clearTimeout(this.#autoCloseTimeout);
        this.#autoCloseTimeout = undefined;
      }
    } catch (error) {
      console.error('Error in add-to-cart-notification disconnectedCallback:', error);
    }
  }

  /**
   * Handles the cart add event to show the notification.
   * @param {CartAddEvent} event - The cart add event.
   */
  #handleCartAdd = (event) => {
    try {
      // Validate event structure
      if (!event || !event.detail) {
        return;
      }

      // Only show notification for successful additions
      if (event.detail.data?.didError) {
        return;
      }

      // Don't show notification for cart upsell additions
      const source = event.detail.data?.source || /** @type {any} */ (event.detail)?.source;
      if (source === 'cart-upsell') {
        return;
      }

      // Don't show notification for cart items component (deletions/quantity changes)
      if (source === 'cart-items-component') {
        return;
      }

      // Only show notification for actual product form additions
      if (source !== 'product-form-component') {
        return;
      }

      // Use addedProduct from event
      const addedProduct = /** @type {CartItem | undefined} */ (event.detail.data?.addedProduct);
      if (addedProduct && Object.keys(addedProduct).length > 0) {
        this.#showNotification(addedProduct);
      }
    } catch (error) {
      console.error('Error handling cart add event:', error);
    }
  };


  /**
   * Shows the notification with product details.
   * @param {CartItem} cartItem - The cart item to display.
   */
  #showNotification(cartItem) {
    try {
      // Get elements using querySelector since refs are inside dialog-component
      const productImage = /** @type {HTMLImageElement | null} */ (this.querySelector('.add-to-cart-notification__image'));
      const productName = /** @type {HTMLElement | null} */ (this.querySelector('.add-to-cart-notification__product-name'));
      const productPrice = /** @type {HTMLElement | null} */ (this.querySelector('.add-to-cart-notification__product-price'));
      const productSize = /** @type {HTMLElement | null} */ (this.querySelector('.add-to-cart-notification__info-item:nth-child(1)'));
      const productColor = /** @type {HTMLElement | null} */ (this.querySelector('.add-to-cart-notification__info-item:nth-child(2)'));
      const productFourthOption = /** @type {HTMLElement | null} */ (this.querySelector('.add-to-cart-notification__info-item:nth-child(3)'));
      const checkoutButton = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.add-to-cart-notification__checkout'));

      // Validate required elements
      if (!productImage || !productName || !productPrice) {
        console.error('Required notification elements not found');
        return;
      }

      // Set product image
      if (cartItem.image) {
        productImage.src = cartItem.image;
        productImage.loading = 'eager';
        productImage.fetchPriority = 'high';
        productImage.alt = cartItem.product_title || 'Product image';
      }

      // Set product name
      if (cartItem.product_title) {
        productName.textContent = cartItem.product_title.toUpperCase();
      }

      // Set product price
      if (cartItem.price !== undefined) {
        productPrice.textContent = this.#formatPrice(cartItem.price);
      }

      // Set product details using options_with_values if available, otherwise fallback to variant_title
      if (productSize) {
        let sizeValue = '';
        if (cartItem.options_with_values && Array.isArray(cartItem.options_with_values)) {
          const sizeOption = cartItem.options_with_values.find(option => 
            option.name && option.name.toLowerCase().includes('size')
          );
          if (sizeOption && sizeOption.value) {
            sizeValue = sizeOption.value;
          }
        }
        
        // Fallback to variant_title if no size option found
        if (!sizeValue && cartItem.variant_title && cartItem.variant_title !== 'Default Title') {
          sizeValue = cartItem.variant_title;
        }
        
        if (sizeValue) {
          productSize.textContent = `SIZE: ${sizeValue.toUpperCase()}`;
        } else {
          productSize.textContent = '';
        }
      }

      // Set color using options_with_values if available, otherwise fallback to properties
      if (productColor) {
        let colorValue = '';
        if (cartItem.options_with_values && Array.isArray(cartItem.options_with_values)) {
          const colorOption = cartItem.options_with_values.find(option => 
            option.name && option.name.toLowerCase().includes('color')
          );
          if (colorOption && colorOption.value) {
            colorValue = colorOption.value;
          }
        }
        
        // Fallback to properties or variant_title parsing
        if (!colorValue) {
          colorValue = cartItem.properties?.Color || cartItem.variant_title?.match(/color:\s*([^,]+)/i)?.[1] || '';
        }
        
        if (colorValue) {
          productColor.textContent = `COLOR: ${colorValue.toUpperCase()}`;
        } else {
          productColor.textContent = '';
        }
      }

      // Set fourth option property (Game of the Week)
      if (productFourthOption) {
        let fourthOptionValue = '';
        if (cartItem.properties) {
          // Check for "Game of the Week" property
          fourthOptionValue = cartItem.properties['Game of the Week'] || 
                             Object.values(cartItem.properties).find((val, idx) => 
                               Object.keys(cartItem.properties)[idx]?.toLowerCase().includes('game')
                             ) || '';
        }
        
        if (fourthOptionValue) {
          const propertyName = Object.keys(cartItem.properties || {}).find(key => 
            cartItem.properties?.[key] === fourthOptionValue
          ) || 'GAME';
          productFourthOption.textContent = `${propertyName.toUpperCase()}: ${fourthOptionValue.toUpperCase()}`;
        } else {
          productFourthOption.textContent = '';
        }
      }

      // Update checkout button URL
      if (checkoutButton) {
        checkoutButton.href = '/cart';
      }

      // Show the dialog
      this.#showDialog();

      // Auto-close after 5 seconds
      this.#autoCloseTimeout = setTimeout(() => {
        this.close();
      }, 5000);
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  /**
   * Shows the notification dialog with slide-down animation.
   */
  #showDialog() {
    try {
      // Validate dialog component exists
      if (!this.refs.notificationDialog) {
        console.error('Notification dialog component not found');
        return;
      }

      // Show the dialog using the dialog-component
      // this.refs.notificationDialog.showDialog();

      // Prevent cart drawer from auto-opening by temporarily disabling it
      this.#preventCartDrawerAutoOpen();
    } catch (error) {
      console.error('Error showing dialog:', error);
    }
  }

  /**
   * Prevents the cart drawer from auto-opening by temporarily removing the auto-open attribute.
   */
  #preventCartDrawerAutoOpen() {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component[auto-open]');
      if (cartDrawer) {
        cartDrawer.setAttribute('data-temp-auto-open', 'true');
        cartDrawer.removeAttribute('auto-open');
      }
    } catch (error) {
      console.error('Error preventing cart drawer auto-open:', error);
    }
  }

  /**
   * Restores the cart drawer auto-open functionality.
   */
  #restoreCartDrawerAutoOpen() {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component[data-temp-auto-open]');
      if (cartDrawer) {
        cartDrawer.setAttribute('auto-open', '');
        cartDrawer.removeAttribute('data-temp-auto-open');
      }
    } catch (error) {
      console.error('Error restoring cart drawer auto-open:', error);
    }
  }

  /**
   * Closes the notification dialog.
   */
  close() {
    try {
      // Close the dialog using the dialog-component
      console.log('closing notification',this.refs.notificationDialog);
      if (this.refs.notificationDialog) {
        this.refs.notificationDialog.closeDialog();
      }
      
      // Restore cart drawer auto-open functionality
      // this.#restoreCartDrawerAutoOpen();
    } catch (error) {
      console.error('Error closing notification:', error);
    }
  }

  /**
   * Opens the cart drawer.
   */
  openCartDrawer() {
    try {
      const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.querySelector('cart-drawer-component'));
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
      this.close();
    } catch (error) {
      console.error('Error opening cart drawer:', error);
    }
  }

  /**
   * Formats a price value to currency string.
   * @param {number} price - The price in cents.
   * @returns {string} The formatted price string.
   */
  #formatPrice(price) {
    try {
      // Validate price input
      if (typeof price !== 'number' || isNaN(price)) {
        console.warn('Invalid price value:', price);
        return '$0.00';
      }

      // Use Shopify's money formatting if available
      if (window.Shopify && typeof /** @type {any} */ (window.Shopify).formatMoney === 'function') {
        return /** @type {any} */ (window.Shopify).formatMoney(price);
      }
      
      // Fallback formatting
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'INR'
      }).format(price / 100);
    } catch (error) {
      console.error('Error formatting price:', error);
      return '$0.00';
    }
  }
}

if (!customElements.get('add-to-cart-notification-component')) {
  customElements.define('add-to-cart-notification-component', AddToCartNotificationComponent);
}
